// src/contexts/DataContext.tsx
// Provides municipal-level data backed by Parquet bundles
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  ReactNode
} from 'react';
import {
  DataRecord,
  VariableMetadata,
  VariableCategory
} from '../types/DataTypes';
import {
  loadMunicipalDatasetFromParquet,
  MunicipalRecord,
  MunicipalIndicatorMetadata,
  MunicipalDomainMetadata
} from '../utils/parquetDataLoader';
import { getMunicipalityNames } from '../utils/municipalityNamesLoader';
import { DataFilters } from '../types/FilterTypes';
import { useMapState } from '../hooks/useMapState';

interface ValidationResult {
  validRecords: DataRecord[];
  invalidRecords: unknown[];
  warnings: Array<{ message: string; record?: unknown }>;
  errors: Array<{ message: string; record?: unknown }>;
  totalRecords: number;
  validRecordCount: number;
  invalidRecordCount: number;
  warningCount: number;
  errorCount: number;
}

interface DataContextType {
  data: DataRecord[];
  isLoading: boolean;
  error: Error | null;
  variables: VariableMetadata[];
  years: number[];
  regions: { iso: string; name: string }[];
  countries: string[];
  filteredData: DataRecord[];
  validationResult: ValidationResult | null;
  validationReport: string | null;
  setFilters: (filters: DataFilters) => void;
  currentFilters: DataFilters;
  loadYearData: (year: number) => Promise<void>;
}

const DOMAIN_TO_CATEGORY: Record<string, VariableCategory> = {
  climate: VariableCategory.AMBIENTE,
  emissions: VariableCategory.AMBIENTE,
  airquality: VariableCategory.AMBIENTE,
  geo: VariableCategory.PAESAGGIO
};

const COLOR_FALLBACK: Record<string, string> = {
  climate: '#2c7fb8',
  emissions: '#d95f0e',
  geo: '#31a354'
};

const DataContext = createContext<DataContextType | undefined>(undefined);

function deriveFormat(unit?: string): string {
  if (!unit) return '.2f';
  const lower = unit.toLowerCase();
  if (lower.includes('%')) return '.1f';
  if (lower.includes('€') || lower.includes('eur')) return ',.0f';
  if (lower.includes('index')) return '.1f';
  return '.2f';
}

function convertToDataRecords(
  records: MunicipalRecord[],
  municipalityNames?: Map<string, string>
): DataRecord[] {
  console.log('🔍 Converting records to DataRecords:', {
    totalRecords: records.length,
    sampleRecords: records.slice(0, 3).map(r => ({
      year: r.year,
      yearType: typeof r.year,
      PRO_COM: r.PRO_COM,
      allKeys: Object.keys(r).slice(0, 10)
    })),
    yearStats: {
      validYears: records.filter(r => typeof r.year === 'number' && !Number.isNaN(r.year)).length,
      invalidYears: records.filter(r => !(typeof r.year === 'number' && !Number.isNaN(r.year))).length,
      uniqueYearValues: [...new Set(records.map(r => r.year))].slice(0, 10)
    }
  });

  return records
    .filter((record) => typeof record.year === 'number' && !Number.isNaN(record.year))
    .map((record) => {
      const rawProCom = record.PRO_COM ?? '';
      const proCom = rawProCom.trim().padStart(6, '0');
      const provinceCodeValue = Number.parseInt(proCom, 10);
      const provinceCode = Number.isNaN(provinceCodeValue) ? 0 : provinceCodeValue;
      const municipalityName = municipalityNames?.get(proCom)
        || (typeof record.municipality_name === 'string' && record.municipality_name.trim() !== ''
          ? record.municipality_name.trim()
          : null)
        || proCom;
      const provinceSigla = proCom.slice(0, 3) || 'UNK';
      const nutsCode = record.nuts3_code && typeof record.nuts3_code === 'string'
        ? record.nuts3_code
        : proCom;
      const yearValue = record.year as number;

      return {
        ...record,
        province_code: provinceCode,
        province_name: municipalityName,
        province_sigla: provinceSigla,
        nuts3_code: nutsCode,
        year: yearValue,
        PRO_COM: proCom,
        municipality_name: municipalityName,
        iso_r: nutsCode,
        country: 'Italy'
      } as DataRecord;
    });
}

function buildVariableMetadata(
  indicators: Record<string, MunicipalIndicatorMetadata>,
  categories: Record<string, MunicipalDomainMetadata>
): VariableMetadata[] {
  return Object.entries(indicators)
    .map(([id, meta]) => {
      const domainKey = meta.category;
      const category = DOMAIN_TO_CATEGORY[domainKey] ?? VariableCategory.OTHER;
      const domain = categories[domainKey];
      const label = meta.name || id;
      const description = meta.description || label;
      const color = domain?.color || COLOR_FALLBACK[domainKey] || '#6c757d';

      return {
        id,
        code: id,
        label,
        description,
        name_en: label,
        name_it: label,
        description_en: description,
        description_it: description,
        unit: meta.unit,
        format: deriveFormat(meta.unit),
        category,
        colorScale: domainKey === 'climate' ? 'viridis' : domainKey === 'geo' ? 'blues' : 'inferno',
        invertScale: false,
        visible: true,
        domain_color: color,
        domain: domainKey,
        sourceFile: meta.sourceFile,
        timeInvariant: meta.timeInvariant,
        aggregatable: !meta.unit?.includes('%'),
        order: meta.order
      } as VariableMetadata;
    })
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

function extractYears(records: DataRecord[]): number[] {
  const years = new Set<number>();
  records.forEach((record) => {
    if (typeof record.year === 'number' && !Number.isNaN(record.year)) {
      years.add(record.year);
    }
  });
  return Array.from(years).sort((a, b) => a - b);
}

function extractRegions(records: DataRecord[]): { iso: string; name: string }[] {
  const entries = new Map<string, string>();
  records.forEach((record) => {
    // Use municipality code as iso, but proper name as display name
    const code = record.PRO_COM || record.nuts3_code || record.iso_r;
    if (code && !entries.has(code)) {
      // Try to get human-readable name, fall back to code if not available
      const name = record.municipality_name || record.province_name || code;
      entries.set(code, name);
    }
  });
  return Array.from(entries.entries())
    .map(([iso, name]) => ({ iso, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export const DataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [data, setData] = useState<DataRecord[]>([]);
  const [filteredData, setFilteredData] = useState<DataRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [years, setYears] = useState<number[]>([]);
  const [regions, setRegions] = useState<{ iso: string; name: string }[]>([]);
  const [countries, setCountries] = useState<string[]>(['Italy']);
  const [variables, setVariables] = useState<VariableMetadata[]>([]);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [validationReport, setValidationReport] = useState<string | null>(null);
  const [currentFilters, setCurrentFilters] = useState<DataFilters>({});
  const [loadedYears, setLoadedYears] = useState<Set<number>>(new Set());

  const loadingCounterRef = useRef(0);
  const loadingYearsRef = useRef<Set<number>>(new Set());
  const municipalityNamesRef = useRef<Map<string, string> | null>(null);
  const preloadInitiatedRef = useRef(false);
  const priority2InitiatedRef = useRef(false);

  // Access selectedVariable from MapStateContext for Priority 2 preloading
  let selectedVariable: string | null = null;
  try {
    const mapState = useMapState();
    selectedVariable = mapState.selectedVariable;
  } catch {
    // useMapState might throw if not within MapStateProvider
    // This is fine - Priority 2 just won't run
    selectedVariable = null;
  }

  const updateLoadingState = useCallback((delta: number) => {
    loadingCounterRef.current = Math.max(loadingCounterRef.current + delta, 0);
    setIsLoading(loadingCounterRef.current > 0);
  }, []);

  const mergeDataRecords = useCallback((existing: DataRecord[], incoming: DataRecord[]): DataRecord[] => {
    if (incoming.length === 0) {
      return existing;
    }

    const map = new Map<string, DataRecord>();
    const buildKey = (record: DataRecord) => `${record.PRO_COM ?? ''}_${record.year ?? 'NA'}`;

    existing.forEach((record) => {
      map.set(buildKey(record), record);
    });

    incoming.forEach((record) => {
      const key = buildKey(record);
      const current = map.get(key);
      if (current) {
        map.set(key, { ...current, ...record });
      } else {
        map.set(key, record);
      }
    });

    return Array.from(map.values()).sort((a, b) => {
      const codeComparison = (a.PRO_COM ?? '').localeCompare(b.PRO_COM ?? '');
      if (codeComparison !== 0) {
        return codeComparison;
      }
      const yearA = a.year ?? 0;
      const yearB = b.year ?? 0;
      return yearA - yearB;
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      try {
        updateLoadingState(1);
        setError(null);

        const dataset = await loadMunicipalDatasetFromParquet();
        if (!municipalityNamesRef.current) {
          try {
            municipalityNamesRef.current = await getMunicipalityNames();
          } catch (municipalityError) {
            console.warn(municipalityError);
            municipalityNamesRef.current = null;
          }
        }
        if (cancelled) return;

        const records = convertToDataRecords(dataset.data, municipalityNamesRef.current ?? undefined);
        console.log('🔄 Converting dataset records:', {
          rawDataLength: dataset.data.length,
          convertedRecordsLength: records.length,
          sampleRawData: dataset.data.slice(0, 2),
          sampleConvertedData: records.slice(0, 2).map(r => ({ year: r.year, PRO_COM: r.PRO_COM, sampleKeys: Object.keys(r).slice(0, 5) }))
        });

        if (records.length === 0) {
          console.error('⚠️ CRITICAL: No records were converted! Raw data sample:', dataset.data.slice(0, 5));
          console.error('Raw data has proper structure?', {
            hasData: Array.isArray(dataset.data),
            dataLength: dataset.data?.length || 0,
            firstRecordKeys: dataset.data?.[0] ? Object.keys(dataset.data[0]) : 'no first record',
            firstRecordSample: dataset.data?.[0] || 'no data'
          });
        }

        if (records.length === 0) {
          throw new Error('No municipal records available');
        }

        const metadata = buildVariableMetadata(dataset.indicators, dataset.categories);
        const uniqueYears = extractYears(records);
        const uniqueRegions = extractRegions(records);

        console.log('📈 Extracted data summary:', {
          recordCount: records.length,
          uniqueYearsCount: uniqueYears.length,
          uniqueYears: uniqueYears,
          uniqueRegionsCount: uniqueRegions.length,
          variablesCount: metadata.length
        });

        console.log('📊 Variables debug:', {
          rawIndicatorsCount: Object.keys(dataset.indicators || {}).length,
          rawCategoriesCount: Object.keys(dataset.categories || {}).length,
          sampleIndicators: Object.entries(dataset.indicators || {}).slice(0, 3),
          sampleVariables: metadata.slice(0, 3).map(v => ({ id: v.id, label: v.label })),
          metadataLength: metadata.length
        });

        console.log('🎭 Regions debug:', {
          uniqueRegionsCount: uniqueRegions.length,
          sampleRegions: uniqueRegions.slice(0, 5),
          sampleRecordCodes: records.slice(0, 5).map(r => ({ PRO_COM: r.PRO_COM, nuts3_code: r.nuts3_code, iso_r: r.iso_r }))
        });

        const bundleSummary = Object.entries(dataset.bundles)
          .map(([bundleId, manifest]) => {
            const sources = manifest.sources.join(', ');
            const mismatch = manifest.validation?.rowCountMatchesBase === false;
            return `• ${bundleId}: ${manifest.rowCount.toLocaleString()} rows (sources: ${sources})${mismatch ? ' ⚠️' : ''}`;
          })
          .join('\n');

        // Use UNION of years from both partition indexes AND actual loaded data
        // dataset.allYears from parquet loader only considers climate/emissions partitions
        // uniqueYears includes years from ALL bundles (geo, demographic, etc.)
        // Merge both to show complete year range across all variables
        const allIndexYears = dataset.allYears || [];
        const allAvailableYears = new Set([...allIndexYears, ...uniqueYears]);
        const availableYears = Array.from(allAvailableYears).sort((a, b) => a - b);

        console.log('📊 DataContext setting years:', {
          availableYears,
          allIndexYears,
          uniqueYears,
          recordCount: records.length,
          mergedYearCount: availableYears.length,
          yearRange: availableYears.length > 0
            ? `${availableYears[0]} - ${availableYears[availableYears.length - 1]}`
            : 'none'
        });

        setData(records);
        setFilteredData(records);
        setVariables(metadata);
        setYears(availableYears);
        setLoadedYears(new Set(uniqueYears));
        setRegions(uniqueRegions);
        setCountries(['Italy']);

        setValidationResult({
          validRecords: records,
          invalidRecords: [],
          warnings: [],
          errors: [],
          totalRecords: records.length,
          validRecordCount: records.length,
          invalidRecordCount: 0,
          warningCount: 0,
          errorCount: 0
        });

        setValidationReport(
          `Loaded ${records.length.toLocaleString()} municipal records\n${bundleSummary}`
        );

        // 🚀 3-TIER PRIORITY SYSTEM: Smart preloading based on user needs
        // Priority 1: Initial map with current year (DONE - already fast ✅)
        // Priority 2: Selected indicator's years (NEW - loads next 🎯)
        // Priority 3: All other background data (gentle ♻️)
        if (!preloadInitiatedRef.current && availableYears.length > 1) {
          preloadInitiatedRef.current = true;
          const currentlyLoadedYears = uniqueYears;
          const remainingYears = availableYears.filter(y => !currentlyLoadedYears.includes(y));

          if (remainingYears.length > 0) {
            // Helper function for gentle batched loading
            const loadYearsBatched = async (years: number[], priorityLabel: string) => {
              const BATCH_SIZE = 2; // Load 2 years at a time to limit main thread blocking
              let successCount = 0;

              console.log(`🚀 Starting ${priorityLabel} preload of ${years.length} years`);

              try {
                // Process years in batches with yielding between batches
                for (let i = 0; i < years.length; i += BATCH_SIZE) {
                  const batch = years.slice(i, i + BATCH_SIZE);

                  // Load current batch in parallel, then yield to browser
                  await new Promise<void>((resolve) => {
                    if ('requestIdleCallback' in window) {
                      // NO timeout - only loads when browser is genuinely idle (prevents freezing)
                      (window as any).requestIdleCallback(async () => {
                        try {
                          const batchPromises = batch.map(async (year) => {
                            try {
                              await loadYearData(year);
                              console.log(`✅ ${priorityLabel} year ${year} (batch ${Math.floor(i / BATCH_SIZE) + 1})`);
                              return true;
                            } catch (err) {
                              console.warn(`⚠️ Failed to preload year ${year}:`, err);
                              return false;
                            }
                          });
                          const results = await Promise.all(batchPromises);
                          successCount += results.filter(Boolean).length;
                        } finally {
                          resolve();
                        }
                      }); // No timeout - browser decides when to execute (gentle batched preloading)
                    } else {
                      // Fallback: Long delay (10s) between batches for gentle preloading
                      setTimeout(async () => {
                        try {
                          const batchPromises = batch.map(async (year) => {
                            try {
                              await loadYearData(year);
                              console.log(`✅ ${priorityLabel} year ${year}`);
                              return true;
                            } catch (err) {
                              console.warn(`⚠️ Failed to preload year ${year}:`, err);
                              return false;
                            }
                          });
                          const results = await Promise.all(batchPromises);
                          successCount += results.filter(Boolean).length;
                        } finally {
                          resolve();
                        }
                      }, 10000); // 10 second delay between batches (2 years per batch, very gentle)
                    }
                  });
                }

                console.log(`✅ ${priorityLabel} complete: ${successCount}/${years.length} years loaded`);
              } catch (err) {
                console.warn(`⚠️ ${priorityLabel} encountered error:`, err);
              }
            };

            // 🎯 PRIORITY 2: Selected indicator years - loads FIRST after initial map
            const startPriority2 = async () => {
              if (!selectedVariable || priority2InitiatedRef.current) {
                console.log('⏭️ Skipping Priority 2 - no selected variable or already initiated');
                return [];
              }

              priority2InitiatedRef.current = true;

              // For now, we'll load ALL remaining years as Priority 2
              // In a more sophisticated implementation, we could query which years have data for selectedVariable
              // But since parquet files are partitioned by year, we need to load them to know
              console.log(`🎯 Priority 2: Preloading years for selected indicator "${selectedVariable}"`);

              // Since we can't know which years have the indicator without loading them,
              // we'll load all years but with Priority 2 label for visibility
              await loadYearsBatched(remainingYears, 'Priority 2 (selected indicator)');

              return remainingYears; // Return loaded years
            };

            // ♻️ PRIORITY 3: All other background data - loads LAST (gentle)
            const startPriority3 = async (alreadyLoadedByPriority2: number[]) => {
              const priority3Years = remainingYears.filter(y => !alreadyLoadedByPriority2.includes(y));

              if (priority3Years.length === 0) {
                console.log('✅ Priority 3 complete - all years already loaded by Priority 2');
                return;
              }

              await loadYearsBatched(priority3Years, 'Priority 3 (background)');
            };

            // Start the priority chain: Priority 2 → Priority 3
            if ('requestIdleCallback' in window) {
              (window as any).requestIdleCallback(async () => {
                console.log('🔄 Starting 3-tier priority preload chain (idle)...');
                const priority2Years = await startPriority2();
                await startPriority3(priority2Years);
                console.log('🎉 All priorities complete!');
              }); // No timeout - truly idle
            } else {
              setTimeout(async () => {
                console.log('🔄 Starting 3-tier priority preload chain (delayed)...');
                const priority2Years = await startPriority2();
                await startPriority3(priority2Years);
                console.log('🎉 All priorities complete!');
              }, 5000); // 5s delay before starting
            }
          }
        }
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err : new Error('Unknown data loading error');
        setError(message);
        setData([]);
        setFilteredData([]);
        setVariables([]);
        setYears([]);
        setRegions([]);
        setLoadedYears(new Set());
        setValidationResult(null);
        setValidationReport(message.message);
      } finally {
        if (!cancelled) {
          updateLoadingState(-1);
        }
      }
    };

    loadData();
    return () => {
      cancelled = true;
    };
  }, []);

  const applyFilters = useCallback(() => {
    if (data.length === 0) {
      setFilteredData([]);
      return;
    }

    let filtered = data;

    if (currentFilters.years && currentFilters.years.length > 0) {
      filtered = filtered.filter((record) =>
        record.year !== undefined && currentFilters.years!.includes(record.year)
      );
    }

    if (currentFilters.regions && currentFilters.regions.length > 0) {
      filtered = filtered.filter((record) => {
        const candidates = [record.PRO_COM, record.nuts3_code, record.iso_r].filter(Boolean) as string[];
        return candidates.some((code) => currentFilters.regions!.includes(code));
      });
    }

    if (currentFilters.countries && currentFilters.countries.length > 0) {
      filtered = filtered.filter((record) => {
        const country = record.country || 'Italy';
        return currentFilters.countries!.includes(country);
      });
    }

    if (currentFilters.customFilter) {
      filtered = filtered.filter(currentFilters.customFilter);
    }

    setFilteredData(filtered);
  }, [data, currentFilters]);

  useEffect(() => {
    applyFilters();
  }, [data, currentFilters]);

  const setFilters = useCallback((filters: DataFilters) => {
    setCurrentFilters((previous) => {
      const next = { ...previous, ...filters };
      const hasChanged =
        JSON.stringify(previous.years || []) !== JSON.stringify(next.years || []) ||
        JSON.stringify(previous.regions || []) !== JSON.stringify(next.regions || []) ||
        JSON.stringify(previous.countries || []) !== JSON.stringify(next.countries || []) ||
        JSON.stringify(previous.variables || []) !== JSON.stringify(next.variables || []) ||
        previous.customFilter !== next.customFilter;

      return hasChanged ? next : previous;
    });
  }, []);

  // Use ref to track loaded years to prevent recreating the callback
  const loadedYearsRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    loadedYearsRef.current = loadedYears;
  }, [loadedYears]);

  const loadYearData = useCallback(async (year: number) => {
    console.log(`🎯 loadYearData called for year ${year}`, {
      alreadyLoaded: loadedYearsRef.current.has(year),
      currentlyLoading: loadingYearsRef.current.has(year),
      callStack: new Error().stack?.split('\n').slice(1, 4)
    });

    if (loadedYearsRef.current.has(year) || loadingYearsRef.current.has(year)) {
      console.log(`⏭️ Skipping year ${year} - already loaded or loading`);
      return;
    }

    loadingYearsRef.current.add(year);
    updateLoadingState(1);

    try {
      const dataset = await loadMunicipalDatasetFromParquet({ targetYear: year });

      if (!municipalityNamesRef.current) {
        try {
          municipalityNamesRef.current = await getMunicipalityNames();
        } catch (municipalityError) {
          console.warn(municipalityError);
          municipalityNamesRef.current = null;
        }
      }

      const records = convertToDataRecords(dataset.data, municipalityNamesRef.current ?? undefined);
      const incomingYears = extractYears(records);

      if (incomingYears.length > 0) {
        setYears((previousYears) => {
          const merged = new Set(previousYears);
          incomingYears.forEach((incomingYear) => merged.add(incomingYear));
          return Array.from(merged).sort((a, b) => a - b);
        });

        setLoadedYears((previousYears) => {
          const merged = new Set(previousYears);
          incomingYears.forEach((incomingYear) => merged.add(incomingYear));
          return merged;
        });
      }

      let mergedData: DataRecord[] = [];
      setData((previousData) => {
        mergedData = mergeDataRecords(previousData, records);
        return mergedData;
      });

      const incomingRegions = extractRegions(records);
      if (incomingRegions.length > 0) {
        setRegions((previousRegions) => {
          const regionMap = new Map(previousRegions.map((region) => [region.iso, region.name]));
          incomingRegions.forEach((region) => {
            if (!regionMap.has(region.iso)) {
              regionMap.set(region.iso, region.name);
            }
          });
          return Array.from(regionMap.entries())
            .map(([iso, name]) => ({ iso, name }))
            .sort((a, b) => a.name.localeCompare(b.name));
        });
      }

      if (variables.length === 0 && dataset.indicators && dataset.categories) {
        const metadata = buildVariableMetadata(dataset.indicators, dataset.categories);
        setVariables(metadata);
      }

      if (mergedData.length > 0) {
        setValidationResult((previous) => {
          if (!previous) {
            return previous;
          }
          return {
            ...previous,
            validRecords: mergedData,
            totalRecords: mergedData.length,
            validRecordCount: mergedData.length
          };
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown data loading error'));
    } finally {
      loadingYearsRef.current.delete(year);
      updateLoadingState(-1);
    }
  }, [mergeDataRecords, updateLoadingState]); // Removed loadedYears to prevent infinite loop

  const contextValue: DataContextType = useMemo(() => ({
    data,
    isLoading,
    error,
    variables,
    years,
    regions,
    countries,
    filteredData,
    validationResult,
    validationReport,
    setFilters,
    currentFilters,
    loadYearData
  }), [
    data,
    isLoading,
    error,
    variables,
    years,
    regions,
    countries,
    filteredData,
    validationResult,
    validationReport,
    setFilters,
    currentFilters,
    loadYearData
  ]);

  return (
    <DataContext.Provider value={contextValue}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = (): DataContextType => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export default DataContext;
